import React, { useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import authService from "@/services/authService";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSeparator,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import { useTheme } from "@/hooks/useTheme";

// Step type for the forgot password flow
type ForgotPasswordStep = "email" | "otp";

export function ForgotPasswordForm() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [forgotPasswordStep, setForgotPasswordStep] =
    useState<ForgotPasswordStep>("email");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { isDark } = useTheme();
  // Email form validation
  const emailForm = useFormik({
    initialValues: {
      email: "",
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .email(t("validation.invalidEmailFormat"))
        .required(t("validation.emailRequired")),
    }),
    onSubmit: async (values) => {
      setIsLoading(true);
      setError(null);
      try {
        // Call authService to request password reset OTP
        await authService.requestPasswordResetOTP(values.email);
        setForgotPasswordStep("otp");
      } catch (error) {
        if (error instanceof Error) {
          setError(error.message);
        } else {
          setError(t("auth.forgotPasswordError"));
        }
      } finally {
        setIsLoading(false);
      }
    },
  });

  // OTP form validation
  const otpForm = useFormik({
    initialValues: {
      otp: "",
    },
    validationSchema: Yup.object({
      otp: Yup.string()
        .matches(/^\d{6}$/, t("validation.otpFormat"))
        .required(t("validation.otpRequired")),
    }),
    onSubmit: async (values) => {
      setIsLoading(true);
      setError(null);
      try {
        // Verify OTP and redirect to reset password page
        await authService.verifyPasswordResetOTP({
          email: emailForm.values.email,
          otp: values.otp,
        });

        // Redirect to reset password page with email in query params
        navigate(
          `/reset-password?email=${encodeURIComponent(emailForm.values.email)}`,
        );
      } catch (error) {
        if (error instanceof Error) {
          setError(error.message);
        } else {
          setError(t("auth.otpVerificationError"));
        }
      } finally {
        setIsLoading(false);
      }
    },
  });

  // Reset back to email step
  const handleResetFlow = () => {
    setForgotPasswordStep("email");
    otpForm.resetForm();
    setError(null);
  };

  return (
    <Card className="w-full">
      <CardHeader className="space-y-1">
        <div className="flex justify-center gap-2 mb-4">
          <Link to="/" className="flex justify-center gap-2 mb-4">
            <img
              src={
                isDark
                  ? "/assets/logo_full_dark.webp"
                  : "/assets/logo_full_light.webp"
              }
              alt="logo"
              className="h-14 transition-all duration-300 "
            />
          </Link>
        </div>
        <CardTitle className="text-2xl text-center">
          {forgotPasswordStep === "email"
            ? t("auth.forgotPasswordTitle")
            : t("auth.enterOtpTitle")}
        </CardTitle>
        <CardDescription className="text-center">
          {forgotPasswordStep === "email"
            ? t("auth.emailPrompt")
            : t("auth.otpSentTo", { email: emailForm.values.email })}
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4">
        {error && (
          <div className="p-3 text-sm text-destructive bg-destructive/10 rounded-md">
            {error}
          </div>
        )}

        {forgotPasswordStep === "email" ? (
          <form onSubmit={emailForm.handleSubmit}>
            <div className="grid gap-2">
              <Label htmlFor="email">{t("auth.email")}</Label>
              <Input
                id="email"
                name="email"
                type="email"
                placeholder={t("auth.placeholder.email")}
                value={emailForm.values.email}
                onChange={emailForm.handleChange}
                onBlur={emailForm.handleBlur}
                disabled={isLoading}
              />
              {emailForm.touched.email && emailForm.errors.email && (
                <p className="text-sm text-destructive">
                  {emailForm.errors.email}
                </p>
              )}
              <Button
                className="w-full mt-4"
                type="submit"
                disabled={isLoading}
              >
                {isLoading ? t("common.processing") : t("auth.sendResetCode")}
              </Button>
            </div>
          </form>
        ) : (
          <form onSubmit={otpForm.handleSubmit}>
            <div className="grid gap-2">
              <Label htmlFor="otp">{t("auth.verificationCode")}</Label>
              <div className="flex justify-center">
                <InputOTP
                  maxLength={6}
                  value={otpForm.values.otp}
                  onChange={(value: string) =>
                    otpForm.setFieldValue("otp", value)
                  }
                  disabled={isLoading}
                >
                  <InputOTPGroup>
                    <InputOTPSlot index={0} />
                    <InputOTPSlot index={1} />
                    <InputOTPSlot index={2} />
                  </InputOTPGroup>
                  <InputOTPSeparator />
                  <InputOTPGroup>
                    <InputOTPSlot index={3} />
                    <InputOTPSlot index={4} />
                    <InputOTPSlot index={5} />
                  </InputOTPGroup>
                </InputOTP>
              </div>
              {otpForm.touched.otp && otpForm.errors.otp && (
                <p className="text-sm text-destructive">{otpForm.errors.otp}</p>
              )}
              <Button
                className="w-full mt-4"
                type="submit"
                disabled={
                  isLoading ||
                  !otpForm.values.otp ||
                  otpForm.values.otp.length < 6
                }
              >
                {isLoading
                  ? t("common.verifying")
                  : t("auth.verifyAndContinue")}
              </Button>
              <Button
                variant="ghost"
                className="mt-2"
                type="button"
                onClick={handleResetFlow}
                disabled={isLoading}
              >
                {t("auth.backToEmail")}
              </Button>
            </div>
          </form>
        )}
      </CardContent>
      <CardFooter className="flex flex-col">
        <div className="mt-2 text-center text-sm">
          {t("auth.rememberPassword")}{" "}
          <a href="/login" className="underline text-primary">
            {t("auth.signIn")}
          </a>
        </div>
      </CardFooter>
    </Card>
  );
}
